/**
 * Public Explore posts hold only what the card needs. Keep this list in sync with
 * communityPostFields() in firestore.rules.
 */
export const COMMUNITY_POST_FIELDS = [
  'userId',
  'designId',
  'displayName',
  'mainImage',
  'title',
  'garmentType',
  'color',
  'material',
  'style',
  'designPlacement',
  'allowRemix',
  'likeCount',
  'createdAt',
  'updatedAt'
]

/** Added after launch. Rules deployed before them reject these keys. */
export const NEWER_COMMUNITY_POST_FIELDS = ['material', 'style', 'designPlacement', 'allowRemix']

export const COMMUNITY_PLACEMENTS = ['front', 'back', 'both', 'none']

export const DEFAULT_COMMUNITY_NAME = 'Style AI designer'
export const SHARE_CONFIRM_MESSAGE = 'This design will be public on Explore'

export function communityPostId(uid, designId) {
  return `${uid}_${designId}`
}

function cleanText(value, max) {
  return typeof value === 'string' ? value.trim().slice(0, max) : ''
}

/** Only a name the customer typed into Settings; never their email or provider name. */
export function communityDisplayName(post) {
  return cleanText(post?.displayName, 60) || DEFAULT_COMMUNITY_NAME
}

function cleanPlacement(value) {
  return COMMUNITY_PLACEMENTS.includes(value) ? value : ''
}

/** Posts shared before the setting was copied onto them stay open to remixes. */
export function canRemixPost(post) {
  return post?.allowRemix !== false
}

export function buildCommunityPost(design, user, mainImage, { allowRemix = true } = {}) {
  const specs = design?.specifications || {}
  const garmentType = cleanText(specs.type, 60)
  const color = cleanText(specs.color, 60)
  const material = cleanText(specs.material, 60)
  const style = cleanText(specs.style, 60)
  const designPlacement = cleanPlacement(specs.designPlacement)
  const displayName = cleanText(user?.communityName, 60)
  return {
    userId: user.uid,
    designId: String(design.id),
    mainImage,
    title: cleanText(design.title, 120) || `${garmentType || 'Custom'} design`,
    garmentType: garmentType || null,
    color: color || null,
    material: material || null,
    style: style || null,
    designPlacement: designPlacement || null,
    allowRemix: allowRemix !== false,
    ...(displayName ? { displayName } : {})
  }
}

/**
 * Starting point for a remix, read from a post's public fields only. Older posts kept
 * these under specifications; prompts and notes are never read.
 */
export function remixFromPost(post) {
  const specs = post?.specifications && typeof post.specifications === 'object' ? post.specifications : {}
  const pick = (field, legacyField) => cleanText(post?.[field], 60) || cleanText(specs[legacyField], 60)
  return {
    postId: String(post?.id || ''),
    title: cleanText(post?.title, 120),
    mainImage: typeof post?.mainImage === 'string' ? post.mainImage : '',
    designData: {
      type: pick('garmentType', 'type'),
      color: pick('color', 'color'),
      material: pick('material', 'material'),
      style: pick('style', 'style'),
      designPlacement: cleanPlacement(post?.designPlacement) || cleanPlacement(specs.designPlacement)
    }
  }
}

/** True for posts that belong to this design, including older posts keyed by design id. */
export function isPostForDesign(post, uid, designId) {
  if (!post || post.userId !== uid || !designId) return false
  const id = String(designId)
  return post.designId === id || post.id === id || post.id === communityPostId(uid, id)
}
